import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import type { IUser } from "../../../model/model";
import type { IProjectGroupWithProjects } from "../../projectGroups/state/projectGroupsSlice";

/**
 * Redux state for the profile-page MFA enrollment/management UI
 * (docs/plans/todo/creator-mfa-two-factor-auth.md, Phase 6).
 *
 * `enrollment` and `recoveryCodes` are the only place the TOTP secret and the
 * one-time-shown recovery codes ever live on the client. Both are cleared by
 * `clearEnrollmentWizardState` when the enroll/recovery-codes dialogs close —
 * see the Anti-Goals in Phase 6: they must never reach localStorage,
 * sessionStorage, or any cache, and the Redux logger is on in dev.
 */

export type MfaMethod = "totp" | "email";

export interface IMfaStatus {
  enabled: boolean;
  method: MfaMethod | null;
  mandatory: boolean;
  recoveryCodesRemaining: number | null;
}

export interface IMfaEnrollmentMaterial {
  /** Returned by every enroll/start call, JWT or mfa_token alike — the
   * pending challenge this enrollment lives under. enroll/confirm's
   * resolveMfaActor tries mfa_token FIRST (see resolveMfaActor.js), so this
   * must be sent back on confirm even on the JWT (Profile) path — omitting
   * it makes confirm resolve `challenge: null` and fail unconditionally. */
  mfaToken: string;
  method: MfaMethod;
  /** TOTP only — base32 secret, shown as selectable text for manual entry. */
  secret?: string;
  otpauthUri?: string;
  qrDataUri?: string;
}

interface IMfaState {
  status: IMfaStatus | null;
  enrollment: IMfaEnrollmentMaterial | null;
  recoveryCodes: string[] | null;
}

const initialState: IMfaState = {
  status: null,
  enrollment: null,
  recoveryCodes: null,
};

interface IMfaStatusResponse {
  enabled: boolean;
  method: MfaMethod | null;
  mandatory: boolean;
  recovery_codes_remaining: number | null;
}

interface IMfaEnrollStartResponse {
  mfa_token: string;
  method: MfaMethod;
  secret?: string;
  otpauth_uri?: string;
  qr_data_uri?: string;
}

// mfaStepUpSend.js's response - "an email factor is not a one-way door"
// addendum. Mints an in-flight challenge on demand so an email-method holder
// can prove their current factor without a recovery code: the returned
// opaque token is paired with the emailed OTP and sent back to
// enroll/start, disable, or recovery-codes/regenerate INSTEAD of
// recovery_code (see those three thunks' `stepUpToken` argument below).
interface IMfaStepUpSendResponse {
  ok: boolean;
  step_up_token: string;
}

// mfaEnrollConfirm.js's response shape depends on `via` (Appendix D):
//  - Profile flow (JWT): { ok, recovery_codes }.
//  - Login flow (mfa_token from a mandated first-login enrollment, Phase 7):
//    the FULL login payload (same shape as a normal /auth/creator success)
//    plus `recovery_codes` — the admin lands enrolled AND logged in from one
//    confirm call. `jwt` is the discriminator LoginForm.tsx uses to tell the
//    two apart; only the fields this slice/dialog actually reads are typed.
export interface IMfaEnrollConfirmResponse {
  ok?: boolean;
  recovery_codes: string[];
  jwt?: string | null;
  helpDocsJwt?: string;
  user?: IUser;
  firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
}

interface IMfaRecoveryCodesResponse {
  recovery_codes: string[];
}

// Strapi v4 error bodies look like { error: { message } } (loaderHandleMiddleware's
// Priority 1) — forwarding response.data via rejectWithValue lets that machinery
// and any bespoke .catch() in a component read the real backend message, mirroring
// forgottenPassword/resetPassword in authSlice.ts. Inlined per-thunk (rather than
// a shared helper) so each thunk's real `rejectWithValue` keeps its RTK-inferred
// type instead of being widened through an intermediate `unknown`-typed wrapper.

export const fetchMfaStatus = createAsyncThunk<IMfaStatusResponse, IAppLoaderAction>(
  "mfa/fetchStatus",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.get(apiUrls.mfa.status);
      return res.data as IMfaStatusResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const startMfaEnrollment = createAsyncThunk<
  IMfaEnrollStartResponse,
  {
    method: MfaMethod;
    /** Profile-initiated (JWT) flow only — resolveMfaActor.js requires the
     * current password to re-authenticate a sensitive change. */
    password?: string;
    /** Login-initiated flow (Phase 7, D6 mandate): the mfa_token from leg 1's
     * mfa_enrollment_required response. When present, resolveMfaActor.js
     * resolves the actor from it directly and does NOT ask for the password
     * again — it was already verified moments earlier in leg 1. */
    mfaToken?: string;
    /** Only required when replacing an already-confirmed factor — D16. */
    currentOtp?: string;
    recoveryCode?: string;
    /** Email step-up (mfaStepUpSend.js): pairs with `currentOtp` as the
     * email-credential equivalent of a bare TOTP `currentOtp` — see that
     * thunk's doc. Only meaningful when the EXISTING credential is email. */
    stepUpToken?: string;
  } & IAppLoaderAction
>(
  "mfa/startEnrollment",
  async (
    { method, password, mfaToken, currentOtp, recoveryCode, stepUpToken },
    { rejectWithValue },
  ) => {
    try {
      const res = await axios.post(apiUrls.mfa.enrollStart, {
        method,
        password,
        mfa_token: mfaToken,
        current_otp: currentOtp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data as IMfaEnrollStartResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const confirmMfaEnrollment = createAsyncThunk<
  IMfaEnrollConfirmResponse,
  { mfaToken: string; otp: string } & IAppLoaderAction
>("mfa/confirmEnrollment", async ({ mfaToken, otp }, { rejectWithValue }) => {
  try {
    const res = await axios.post(apiUrls.mfa.enrollConfirm, { mfa_token: mfaToken, otp });
    return res.data as IMfaEnrollConfirmResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err?.response?.data);
    }
    throw err;
  }
});

// Both disable and regenerate accept EITHER a current code (`otp`) or a
// recovery code (`recovery_code`) alongside the password — mfaDisable.js /
// mfaRegenerateRecoveryCodes.js each check recovery_code first, falling back
// to otp only for a totp credential (an email credential has no in-flight
// challenge to check an OTP against outside login/enrollment, so a recovery
// code is its only disable/regenerate path).
interface ICredentialConfirmArgs {
  password: string;
  otp?: string;
  recoveryCode?: string;
  /** Email step-up (mfaStepUpSend.js): pairs with `otp` as the
   * email-credential equivalent of a bare TOTP `otp`. */
  stepUpToken?: string;
}

export const disableMfa = createAsyncThunk<unknown, ICredentialConfirmArgs & IAppLoaderAction>(
  "mfa/disable",
  async ({ password, otp, recoveryCode, stepUpToken }, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.disable, {
        password,
        otp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const regenerateRecoveryCodes = createAsyncThunk<
  IMfaRecoveryCodesResponse,
  ICredentialConfirmArgs & IAppLoaderAction
>(
  "mfa/regenerateRecoveryCodes",
  async ({ password, otp, recoveryCode, stepUpToken }, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.regenerateRecoveryCodes, {
        password,
        otp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data as IMfaRecoveryCodesResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

// "an email factor is not a one-way door" addendum: mfaEnrollStart.js (D16
// replacement), mfaDisable.js and mfaRegenerateRecoveryCodes.js all accept
// `current_otp`/`otp` as proof of the EXISTING factor only when that factor
// is TOTP - an email credential has no in-flight challenge to check a code
// against outside a login, so a recovery code used to be the only way to
// ever change or disable an email factor. This mints that in-flight
// challenge on demand (mfaStepUpSend.js): JWT-authed, empty body, returns an
// opaque `step_up_token` that the three flows above now also accept, paired
// with the freshly emailed OTP, as an alternative to a recovery code.
export const sendMfaStepUpCode = createAsyncThunk<IMfaStepUpSendResponse, IAppLoaderAction>(
  "mfa/sendStepUpCode",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.stepUpSend, {});
      return res.data as IMfaStepUpSendResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // Surface the HTTP status alongside the body (reportsSlice.ts
        // precedent) - 429 (Appendix E's shared 3-per-15-min email-OTP
        // bucket) is a real, honestly-reported limit here (unlike the
        // anti-enumeration login sends), so a caller could branch on it.
        return rejectWithValue({
          ...(err.response?.data as Record<string, unknown> | undefined),
          status: err.response?.status ?? null,
        });
      }
      throw err;
    }
  },
);

export const mfaSlice = createSlice({
  name: "mfa",
  initialState,
  reducers: {
    // Wizard/dialog teardown. Called on every close of MfaEnrollDialog and
    // RecoveryCodesDialog so the secret/codes never outlive the component
    // that needed them (Phase 6 Anti-Goals).
    clearEnrollmentWizardState: (state) => {
      state.enrollment = null;
      state.recoveryCodes = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMfaStatus.fulfilled, (state, { payload }) => {
      state.status = {
        enabled: payload.enabled,
        method: payload.method,
        mandatory: payload.mandatory,
        recoveryCodesRemaining: payload.recovery_codes_remaining ?? null,
      };
    });

    builder.addCase(startMfaEnrollment.fulfilled, (state, { payload }) => {
      state.enrollment = {
        mfaToken: payload.mfa_token,
        method: payload.method,
        secret: payload.secret,
        otpauthUri: payload.otpauth_uri,
        qrDataUri: payload.qr_data_uri,
      };
    });

    builder.addCase(confirmMfaEnrollment.fulfilled, (state, { payload }) => {
      // mfaEnrollConfirm.js's JWT-path response is just { ok, recovery_codes }
      // — no enabled/method here. MfaSection re-fetches status right after
      // (handleEnrolled), which is the source of truth for those fields; we
      // only need to preemptively reflect the new code count.
      if (state.status) {
        state.status.recoveryCodesRemaining = payload.recovery_codes.length;
      }
      state.recoveryCodes = payload.recovery_codes;
      // The secret has done its job (confirmed); drop it immediately rather
      // than waiting for the dialog-close teardown.
      state.enrollment = null;
    });

    builder.addCase(disableMfa.fulfilled, (state) => {
      state.status = {
        enabled: false,
        method: null,
        mandatory: state.status?.mandatory ?? false,
        recoveryCodesRemaining: null,
      };
    });

    builder.addCase(regenerateRecoveryCodes.fulfilled, (state, { payload }) => {
      state.recoveryCodes = payload.recovery_codes;
      if (state.status) {
        state.status.recoveryCodesRemaining = payload.recovery_codes.length;
      }
    });
  },
});

export const mfaReducer = mfaSlice.reducer;
export const { clearEnrollmentWizardState } = mfaSlice.actions;

export const getMfaStatus = (state: RootState) => state.mfa.status;
export const getMfaEnrollment = (state: RootState) => state.mfa.enrollment;
export const getMfaRecoveryCodes = (state: RootState) => state.mfa.recoveryCodes;
